import { Plus, X } from "lucide-react";
import { RazorInputField, RazorNumberFormat } from "../../../../components/razor-reuse";
import DateRangeComponent from "../../../../components/fragment/date-range/DateRangeComponent";
import { DateRangeValue, FilterRuleForm } from "../types";
import { CountryOption } from "../../../../redux/type";
import {
  findCountryOption,
  useNationalityCountries,
} from "./useNationalityCountries";

const RULE_TYPES = [
  ["Country", "Country"],
  ["Birthday Period", "birthday_period"],
  ["Gender", "Gender"],
  ["KYC Status", "kyc_status"],
  ["Bridge Status", "wallet_status"],
  ["Onboarding Date", "onboarding_date"],
];

// Rule types whose value is a list of selections rather than a single one.
const MULTI_SELECT_TYPES = new Set(["Country", "kyc_status", "wallet_status"]);
// Rule types whose value is a { from, to } date range.
const DATE_RANGE_TYPES = new Set(["onboarding_date"]);

// Country options come from the API (useNationalityCountries), not
// RULE_VALUE_OPTIONS. Compared case-insensitively since the saved payload
// lowercases the rule type.
export function isCountryRule(type: string): boolean {
  return type?.toLowerCase() === "country";
}
// Country has its own dedicated branch below (countryPlaceholder) since it
// also needs live-loading/retry state the other multi-selects don't.
const MULTI_SELECT_PLACEHOLDER: Record<string, string> = {
  kyc_status: "Select KYC status...",
  wallet_status: "Select wallet status...",
};

// const RULE_VALUE_OPTIONS: Record<string, string[]> = {
//   Country: ["United Kingdom", "Nigeria", "United States", "Canada", "Germany"],
//   "Birthday Period": ["Next 7 Days", "Next 30 Days", "This Month"],
//   Gender: ["Male", "Female", "Both"],
// };
const RULE_VALUE_OPTIONS: Record<
  string,
  { label: string; value: string }[]
> = {
  "birthday_period": [
    { label: "Next 7 Days", value: "next_7_days" },
    { label: "Next 30 Days", value: "next_30_days" },
    { label: "This Month", value: "this_month" },
  ],

  Gender: [
    { label: "Male", value: "male" },
    { label: "Female", value: "female" },
    // { label: "Both", value: "both" },
  ],

  kyc_status: [
    { label: "Level Zero", value: "level-zero" },
    { label: "Level 1", value: "level-one" },
    { label: "Level 2", value: "level-two" },
  ],

  wallet_status: [
    { label: "No Wallet", value: "no_wallet" },
    { label: "Active", value: "active" },
    { label: "Rejected", value: "rejected" },
    { label: "Pending", value: "pending" },
    { label: "Under Review", value: "under_review" },
    { label: "Incomplete", value: "incomplete" },
    { label: "Awaiting UBO", value: "awaiting_ubo" },
  ],
};

export function isRuleValueEmpty(value: FilterRuleForm["value"]): boolean {
  if (!value) return true;
  if (Array.isArray(value)) return value.length < 1;
  if (typeof value === "object") return !value.from || !value.to;
  return value.length < 1;
}

// Pass `countries` for a country rule to show country names instead of
// iso3 codes; falls back to the raw code for any value not in the list.
export function formatRuleValue(
  value: FilterRuleForm["value"],
  countries?: CountryOption[],
): string {
  if (isRuleValueEmpty(value)) return "—";
  if (Array.isArray(value)) {
    if (!countries) return value.join(", ");
    return value.map((v) => findCountryOption(countries, v)?.label || v).join(", ");
  }
  if (typeof value === "object") return `${value.from} - ${value.to}`;
  if (countries) return findCountryOption(countries, value)?.label || value;
  return value;
}
interface AudienceFilterRulesProps {
  rules: FilterRuleForm[];
  onChange: (rules: FilterRuleForm[]) => void;
  matchedCount?: number;
  loadingMatchedCount?: boolean;
}

export default function AudienceFilterRules({
  rules,
  onChange,
  matchedCount,
  loadingMatchedCount,
}: AudienceFilterRulesProps) {
  const hasCountryRule = rules.some((rule) => isCountryRule(rule.type));
  const {
    countries,
    loading: loadingCountries,
    failed: countriesFailed,
    retry: retryCountries,
  } = useNationalityCountries(hasCountryRule);

  const countryPlaceholder = loadingCountries
    ? "Loading countries..."
    : countriesFailed
      ? "Couldn't load countries"
      : countries.length < 1
        ? "No countries available"
        : "Select countries...";

  const emptyValueFor = (type: string): FilterRuleForm["value"] => {
    if (MULTI_SELECT_TYPES.has(type)) return [];
    if (DATE_RANGE_TYPES.has(type)) return { from: "", to: "" };
    return "";
  };

  const addRule = () => {
    onChange([
      ...rules,
      { id: `rule-${Date.now()}-${rules.length}`, type: RULE_TYPES[0][1], value: "" },
    ]);
  };

  const updateRuleType = (id: string, type: string) => {
    onChange(
      rules.map((rule) =>
        rule.id === id ? { ...rule, type, value: emptyValueFor(type) } : rule,
      ),
    );
  };

  const updateRuleValue = (id: string, value: FilterRuleForm["value"]) => {
    onChange(rules.map((rule) => (rule.id === id ? { ...rule, value } : rule)));
  };

  const removeRule = (id: string) => {
    onChange(rules.filter((rule) => rule.id !== id));
  };

  return (
    <div className="mt-[16px]">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold tracking-wide text-[#71717A] uppercase">
          Filter rules
        </p>
        {rules.length > 0 && (
          <span className="inline-flex items-center gap-[6px] text-[13px] font-medium text-[#16A34A]">
            <span className="size-[6px] rounded-full bg-[#16A34A]" />
            {loadingMatchedCount
              ? "Matching…"
              : `${RazorNumberFormat(matchedCount ?? 0, {
                  hideDecimal: true,
                  hideSymbol: true,
                })} users matched`}
          </span>
        )}
      </div>

      {rules.length > 0 && (
        <div className="mt-[10px] space-y-[10px]">
          {rules.map((rule) => {
            const isMulti = MULTI_SELECT_TYPES.has(rule.type);
            const isDateRange = DATE_RANGE_TYPES.has(rule.type);
            const isCountry = isCountryRule(rule.type);
            const options = RULE_VALUE_OPTIONS[rule.type] || [];

            // Saved codes that aren't in the loaded list (list failed to
            // load, or the country was removed) are kept as their own
            // options so they're still shown and aren't silently dropped
            // from the rule.
            const countryValues = isCountry ? (rule.value as string[]) || [] : [];
            const selectedCountries = countryValues.map(
              (code) => findCountryOption(countries, code) || { label: code, value: code },
            );
            const unknownSelected = selectedCountries.filter(
              (option) => !findCountryOption(countries, option.value),
            );
            const countryOptions =
              unknownSelected.length > 0 ? [...unknownSelected, ...countries] : countries;

            return (
              <div key={rule.id} className="flex items-center gap-[10px]">
                <select
                  value={rule.type}
                  onChange={(e) => updateRuleType(rule.id, e.target.value)}
                  className="h-[40px] w-[180px] shrink-0 rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
                >
                  {RULE_TYPES.map((type) => (
                    <option key={type[0]} value={type[1]}>
                      {type[0]}
                    </option>
                  ))}
                </select>

                <div className="flex-1">
                  {isDateRange ? (
                    <DateRangeComponent
                      value={
                        isRuleValueEmpty(rule.value)
                          ? ""
                          : `${(rule.value as DateRangeValue).from},${(rule.value as DateRangeValue).to}`
                      }
                      onSet={(val: string) => {
                        const [from, to] = val.split(",");
                        updateRuleValue(rule.id, { from, to });
                      }}
                      onCancel={() => updateRuleValue(rule.id, { from: "", to: "" })}
                    />
                  ) : isCountry ? (
                    <>
                      <RazorInputField
                        color="black-light"
                        type="select"
                        multi
                        searchable
                        placeholder={countryPlaceholder}
                        value={selectedCountries}
                        loadingSelect={loadingCountries}
                        disabled={loadingCountries}
                        onChange={(selected: any) =>
                          updateRuleValue(
                            rule.id,
                            (selected || []).map((option: any) => option.value),
                          )
                        }
                        selectOption={countryOptions}
                      />
                      {countriesFailed && (
                        <p className="mt-[4px] text-[12px] text-[#DC2626]">
                          Couldn't load countries.{" "}
                          <button
                            type="button"
                            onClick={retryCountries}
                            className="font-medium underline"
                          >
                            Retry
                          </button>
                        </p>
                      )}
                    </>
                  ) : isMulti ? (
                    <RazorInputField
                      color="black-light"
                      type="select"
                      multi
                      placeholder={MULTI_SELECT_PLACEHOLDER[rule.type] || "Select..."}
                      value={options.filter((option) =>
                        (rule.value as string[]).includes(option.value),
                      )}
                      onChange={(selected: any) =>
                        updateRuleValue(
                          rule.id,
                          (selected || []).map((option: any) => option.value),
                        )
                      }
                      selectOption={options}
                    />
                  ) : (
                    <select
                      value={rule.value as string}
                      onChange={(e) => updateRuleValue(rule.id, e.target.value)}
                      className="h-[40px] w-full rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
                    >
                      <option value="">
                        {rule.type === "Gender" ? "Select gender..." : "Select period"}
                      </option>
                      {options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => removeRule(rule.id)}
                  className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[8px] border border-[#E4E4E7] text-[#A1A1AA] hover:border-[#DC2626] hover:text-[#DC2626]"
                >
                  <X className="size-[14px]" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {rules.length < 1 && (
        <p className="mt-[16px] text-center text-[13px] text-[#A1A1AA]">
          No targeting rules added yet
        </p>
      )}

      <button
        type="button"
        onClick={addRule}
        className="mt-[16px] inline-flex w-full items-center justify-center gap-[6px] rounded-[8px] border border-[#734792] py-[10px] text-[13px] font-medium text-[#734792]"
      >
        <Plus className="size-[14px]" />
        Add {rules.length > 0 ? "another" : "first"} rule
      </button>
    </div>
  );
}
